import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

/** One choice in a {@link StatusSelect}. */
export interface StatusSelectOption {
  value: string;
  label: string;
  /** Optional leading glyph — e.g. the moderation state's coloured icon. */
  icon?: React.ReactNode;
}

export interface StatusSelectProps {
  /** The active value; `undefined` selects the "all" entry. */
  value?: string | undefined;
  /** Fires with `undefined` when the "all" entry is picked. */
  onChange: (value: string | undefined) => void;
  options: StatusSelectOption[];
  /** Label of the "no filter" entry («Все» / «Все продукции»). */
  allLabel: string;
  'aria-label': string;
  /**
   * The trigger's shape, both straight from the design:
   * - `field` — 40px tall, 12px radius, 14px medium. The sidebar's category
   *   status filter (Figma 1224:5202).
   * - `pill` — 49px tall, 24px radius, 16px semibold. The catalog header's
   *   «Все продукции» and the venue card's branch filter (1224:5216 / 1224:5226).
   */
  variant?: 'field' | 'pill';
  className?: string | undefined;
}

/**
 * Sentinel for the "all / no filter" choice — Radix Select forbids an empty
 * value, and `undefined` is how the URL spells "no filter".
 */
const ALL_VALUE = '__all__';

/**
 * The panel's status filter: a white bordered select whose popup is the
 * signature `ActionMenu` card (see `select.tsx`), optionally showing each
 * state's coloured glyph beside its label.
 *
 * Bound by VALUE rather than to a URL param, unlike `Filters.Select` — the
 * screens using it own their own routing (some write a search param, the venue
 * card holds local state), and threading that through a context the venue page
 * has no reason to mount would be the tail wagging the dog.
 */
export function StatusSelect({
  value,
  onChange,
  options,
  allLabel,
  variant = 'field',
  className,
  ...aria
}: StatusSelectProps) {
  const active = options.find((option) => option.value === value);

  return (
    <Select
      value={value ?? ALL_VALUE}
      onValueChange={(next) => onChange(next === ALL_VALUE ? undefined : next)}
    >
      <SelectTrigger
        aria-label={aria['aria-label']}
        className={cn(
          // White bordered field, NOT the default filled pill: on the muted
          // sidebar and inside the white venue card the filled variant either
          // vanished into the surface or read as an input awaiting text.
          'w-full min-w-0 justify-between border-input bg-card font-medium text-foreground shadow-xs',
          '*:data-[slot=select-value]:min-w-0',
          variant === 'field'
            ? 'h-10 rounded-xl px-3 text-sm'
            : 'h-[3.0625rem] rounded-3xl px-4 text-base font-semibold tracking-[-0.04em]',
          className,
        )}
      >
        {/* The trigger shows the active option's own glyph beside its label, so
            the filter reads as the state it is filtering for rather than as a
            bare word — and `SelectValue`'s placeholder covers the "all" case.
            The glyph is rendered HERE, not inside the item's `ItemText`: Radix
            mirrors `ItemText`'s children into the trigger, so an icon placed
            there would be drawn twice — once by the mirror and once by this
            row — with the label pushed onto a second line. */}
        <span className="flex min-w-0 items-center gap-2">
          {active?.icon}
          <SelectValue placeholder={allLabel} />
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_VALUE}>{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} icon={option.icon}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
